import dayjs from 'dayjs';
import { useWeekDays } from '../dates/useWeekDays';
import { formatScheduledHours } from '../../formatting/timeFormatting';
import { formatWeekdayName } from '../../formatting/dateFormatting';
import { useEffect, useState } from 'react';

/**
 * Hook to process shifts for weekly display
 * @param {Array} shifts - Array of shifts to process
 * @param {number} startOfWeek - Unix timestamp for start of week
 * @param {Object} shiftStatus - Status information for shifts
 * @returns {Array} Processed shifts with formatting and status information
 */
export const useProcessedShifts = (shifts, startOfWeek, shiftStatus = {}) => {
  const { weekDays } = useWeekDays(startOfWeek);
  const [processedShifts, setProcessedShifts] = useState([]);

  useEffect(() => {
    const processed = weekDays.map((day) => {
      const dayStart = dayjs(day).startOf('day');
      const dayEnd = dayjs(day).endOf('day');

      // Filter shifts for this day
      const dayShifts = shifts.filter((shift) => {
        const shiftStart = dayjs(shift.start);
        return shiftStart.isBetween(dayStart, dayEnd, null, '[]'); // '[]' means inclusive
      });

      const futureShiftsCount = dayShifts.length;

      // Return early if no shifts
      if (dayShifts.length === 0) {
        return {
          day: formatWeekdayName(day),
          dateNumber: dayjs(day).date(),
          isToday: dayjs(day).isSame(dayjs(), 'day'),
          isPast: dayjs(day).isBefore(dayjs(), 'day'),
          scheduledHours: 'Not scheduled',
          shiftStatus: 'not_scheduled',
          shifts: [],
          futureShiftsCount: 0,
        };
      }

      // Calculate total scheduled minutes
      const totalScheduledMinutes = dayShifts.reduce((total, shift) => {
        const start = dayjs(shift.start);
        const end = dayjs(shift.end);
        return total + end.diff(start, 'minute');
      }, 0);

      // Get shift status
      const shiftDetail = shiftStatus[dayShifts[0].id] || {};
      const status = shiftDetail.status || 'scheduled';

      return {
        day: formatWeekdayName(day),
        dateNumber: dayjs(day).date(),
        isToday: dayjs(day).isSame(dayjs(), 'day'),
        isPast: dayjs(day).isBefore(dayjs(), 'day'),
        scheduledHours: formatScheduledHours(totalScheduledMinutes),
        shiftStatus: status,
        shifts: dayShifts,
        futureShiftsCount,
      };
    });

    setProcessedShifts(processed);
  }, [shifts, shiftStatus, weekDays]);

  return processedShifts;
};
